import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from './components/layout/AppLayout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { RequirePermission } from './components/auth/RequirePermission';
import { FullPageSpinner } from './components/ui';
import { PERMISSIONS } from './config/permissions';
import { LoginPage } from './pages/LoginPage';
import { ForbiddenPage, NotFoundPage } from './pages/ErrorPages';

// Route-level code splitting (spec §42) — each module is its own chunk.
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const PosPage = lazy(() => import('./pages/pos/PosPage').then((m) => ({ default: m.PosPage })));
const KitchenPage = lazy(() => import('./pages/kitchen/KitchenPage').then((m) => ({ default: m.KitchenPage })));
const ActiveOrdersPage = lazy(() => import('./pages/orders/ActiveOrdersPage').then((m) => ({ default: m.ActiveOrdersPage })));
const OrderHistoryPage = lazy(() => import('./pages/orders/OrderHistoryPage').then((m) => ({ default: m.OrderHistoryPage })));
const PaymentsPage = lazy(() => import('./pages/payments/PaymentsPage').then((m) => ({ default: m.PaymentsPage })));
const ProductsPage = lazy(() => import('./pages/catalog/ProductsPage').then((m) => ({ default: m.ProductsPage })));
const CategoriesPage = lazy(() => import('./pages/catalog/CategoriesPage').then((m) => ({ default: m.CategoriesPage })));
const CustomersPage = lazy(() => import('./pages/customers/CustomersPage').then((m) => ({ default: m.CustomersPage })));
const ExpensesPage = lazy(() => import('./pages/expenses/ExpensesPage').then((m) => ({ default: m.ExpensesPage })));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const UsersPage = lazy(() => import('./pages/users/UsersPage').then((m) => ({ default: m.UsersPage })));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const AuditLogPage = lazy(() => import('./pages/audit/AuditLogPage').then((m) => ({ default: m.AuditLogPage })));

const guard = (anyOf: string[], element: React.ReactNode) => (
  <RequirePermission anyOf={anyOf}>
    <Suspense fallback={<FullPageSpinner />}>{element}</Suspense>
  </RequirePermission>
);

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route index element={<Navigate to="/dashboard" replace />} />

          <Route
            path="/dashboard"
            element={guard(
              [PERMISSIONS.DASHBOARD_VIEW, PERMISSIONS.DASHBOARD_VIEW_LIMITED],
              <DashboardPage />,
            )}
          />
          <Route path="/pos" element={guard([PERMISSIONS.ORDERS_CREATE], <PosPage />)} />
          <Route
            path="/orders/active"
            element={guard([PERMISSIONS.ORDERS_VIEW], <ActiveOrdersPage />)}
          />
          <Route path="/kitchen" element={guard([PERMISSIONS.KITCHEN_VIEW], <KitchenPage />)} />
          <Route
            path="/orders/history"
            element={guard([PERMISSIONS.ORDERS_VIEW], <OrderHistoryPage />)}
          />
          <Route
            path="/payments"
            element={guard([PERMISSIONS.PAYMENTS_COLLECT], <PaymentsPage />)}
          />
          <Route
            path="/products"
            element={guard([PERMISSIONS.PRODUCTS_MANAGE], <ProductsPage />)}
          />
          <Route
            path="/categories"
            element={guard([PERMISSIONS.CATEGORIES_MANAGE], <CategoriesPage />)}
          />
          <Route
            path="/customers"
            element={guard([PERMISSIONS.CUSTOMERS_MANAGE], <CustomersPage />)}
          />
          <Route
            path="/expenses"
            element={guard([PERMISSIONS.EXPENSES_MANAGE], <ExpensesPage />)}
          />
          <Route path="/reports" element={guard([PERMISSIONS.REPORTS_VIEW], <ReportsPage />)} />
          <Route path="/users" element={guard([PERMISSIONS.USERS_MANAGE], <UsersPage />)} />
          <Route path="/audit" element={guard([PERMISSIONS.AUDIT_VIEW], <AuditLogPage />)} />
          <Route path="/settings" element={guard([PERMISSIONS.SETTINGS_MANAGE], <SettingsPage />)} />

          <Route path="/403" element={<ForbiddenPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
